<template>
  <form-layout
    :title="receipts && receipts.length > 0 ? 'Receipt' : 'Stop'"
    bottom-bar-stick-bottom
    @on-dismiss="dismiss"
  >
    <vs-wrapper>
      <vs-heading
        v-if="requestedStop"
        class="d-flex heading-overline"
        type="overline"
        data-test="requested-stop-heading"
        >Requested {{ label }}
      </vs-heading>
      <v-list v-if="requestedStop" class="pa-0" data-test="requested-stop-items">
        <v-list-item v-for="item in requestedStopListItems" :key="item.key" class="pl-0 pt-0">
          <v-list-item-icon>
            <v-icon>{{ item.icon }}</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>{{ item.title }}</v-list-item-title>
            <v-list-item-subtitle
              v-for="(subtitle, index) in item.subtitles"
              :key="item.key + '-' + index"
              :data-test="`requested-stop-item-${item.key}-${index}`"
            >
              {{ subtitle }}
            </v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
      </v-list>
      <vs-heading class="d-flex heading-overline" type="overline" data-test="receipt-heading"
        >{{ label }} Confirmation
      </vs-heading>
      <v-list class="pa-0">
        <v-list-item v-for="item in actualStopListItems" :key="item.key" class="pl-0 pt-0">
          <v-list-item-icon>
            <v-icon>{{ item.icon }}</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>{{ item.title }}</v-list-item-title>
            <v-list-item-subtitle
              v-for="(subtitle, index) in item.subtitles"
              :key="item.key + '-' + index"
              :data-test="`receipt-item-${item.key}-${index}`"
              >{{ subtitle }}</v-list-item-subtitle
            >
          </v-list-item-content>
        </v-list-item>
      </v-list>
    </vs-wrapper>
    <template #action-bar>
      <vs-wrapper direction="horizontal">
        <disabled-action-tooltip :permission="editPermission" label="Edit" full-width>
          <template #default="{ disabled }">
            <vs-button
              :disabled="disabled"
              icon="mdi-pen"
              label="Edit"
              type="secondary"
              full-width
              data-test="edit-button"
              @click="editClick"
            />
          </template>
        </disabled-action-tooltip>
        <disabled-action-tooltip :permission="deletePermission" label="Delete" full-width>
          <template #default="{ disabled }">
            <vs-button
              :disabled="disabled"
              :loading="isDeletingActualStop"
              icon="mdi-delete"
              label="Delete"
              type="primary"
              destructive
              full-width
              data-test="delete-button"
              @click="deleteClick"
            />
          </template>
        </disabled-action-tooltip>
      </vs-wrapper>
    </template>
  </form-layout>
</template>
<script setup lang="ts">
import { computed, ref, toRef, unref } from 'vue'
import FormLayout from '@/components/common/FormLayout.vue'
import VsWrapper from '@/components/vision/VsWrapper.vue'
import VsHeading from '@/components/vision/VsHeading.vue'
import VsButton from '@/components/vision/VsButton.vue'
import DisabledActionTooltip from '@/components/common/DisabledActionTooltip.vue'
import { allOf, check, usePermission } from '@/components/common/permission'
import { disabledActionMessages as message } from './disabledActionMessages'
import { useJobBottomSheetStatesStore } from '@/components/jobs/jobBottomSheetStatesStore'
import User from '@/models/user'
import { useStore } from 'vuex-composition-helpers'
import { updateStopReceipts, useGetJobRequestedStopsQuery } from '@/api/jobs'
import { InventoryReceipt, InventoryReceiptType, JobStopType } from '@ironsight/types'
import type { InventoryItem } from '@ironsight/types'
import { buildDisplayInventory, buildInventoryDisplayString } from '@/utils/inventories'
import { useCurrentOrg } from '@/common/useCurrentOrg'
import { useInventoryTypes } from '@/composables/inventory-types/useInventoryTypes'
import { useDeleteActualStopMutation } from '@/api/composables/jobs/stops'
import { useQueryClient } from '@tanstack/vue-query'
import moment from 'moment'
import { useSuggestedInventory } from '@/components/tickets/StopsAndInventories/useInventoryReceiptSuggestedInventory'
import { useJobStopsAndReceiptsPermissions } from '@/components/tickets/StopsAndInventories/useJobStopsAndReceiptsPermissions'

const jobBottomSheetStatesStore = useJobBottomSheetStatesStore()
const props = defineProps<{
  jobId: string
  jobNumber: number
}>()

const jobIdRef = toRef(props, 'jobId')
const jobNumberRef = toRef(props, 'jobNumber')
const { isJobCompleted } = useJobStopsAndReceiptsPermissions(jobNumberRef, ref(null))
const { data: requestedStops } = useGetJobRequestedStopsQuery(jobIdRef)
const { currentOrg } = useCurrentOrg()
const { mutateAsync: deleteActualStop, isLoading: isDeletingActualStop } =
  useDeleteActualStopMutation()
const queryClient = useQueryClient()

const actualStop = computed(
  () => jobBottomSheetStatesStore.jobBottomSheetStates.receiptDetailsBottomSheetStates.actualStop,
)
const receiptType = computed(
  () => jobBottomSheetStatesStore.jobBottomSheetStates.receiptDetailsBottomSheetStates.receiptType,
)
const isPickupStop = computed(() => actualStop.value?.stopType === JobStopType.PickUp)
const label = computed(() => (isPickupStop.value ? 'Pickup' : 'Dropoff'))

const getReceiptType = (receipt: InventoryReceipt): string => {
  if (actualStop.value?.stopType === JobStopType.PickUp) {
    return receipt.receiptType === InventoryReceiptType.Originator ? 'Transfer' : 'Pickup'
  } else {
    return receipt.receiptType === InventoryReceiptType.Originator ? 'Dropoff' : 'Receipt'
  }
}

const getReceiptTime = (receipt: InventoryReceipt): string => {
  const type = getReceiptType(receipt)
  if (type === 'Pickup' || type === 'Dropoff') {
    return actualStop.value?.arrivedAt.format('LLLL') || ''
  } else {
    return receipt.createdAt.format('LLLL')
  }
}

const { suggestedInventory } = useSuggestedInventory(
  jobIdRef,
  actualStop.value?.requestedStopId ?? null,
  actualStop.value?.stopType ?? JobStopType.DropOff,
)

const requestedStop = computed(() =>
  requestedStops.value?.find(
    (requestedStop) => requestedStop.id === actualStop.value?.requestedStopId,
  ),
)

const requestedStopInventory = computed(() => {
  if (!requestedStop.value) {
    return []
  } else if (requestedStop.value.stopType === JobStopType.PickUp) {
    return requestedStop.value.inventory
  } else {
    return suggestedInventory.value
  }
})

const receipts = computed(
  () => jobBottomSheetStatesStore.jobBottomSheetStates.receiptDetailsBottomSheetStates.receipts,
)

const isMyStop = computed(() => {
  return currentOrg.value?.id === getUserBySid.value(actualStop.value?.createdBy)?.organizationId
})
const isMyReceipt = computed(() => {
  if (receipts.value && receipts.value.length > 0) {
    return currentOrg.value?.id === receipts.value[0].ownerOrgId
  } else if (receipts.value && receipts.value.length === 0) {
    // Edge case - Stops with no inventory receipts
    return isMyStop.value
  }

  return false
})
const { permission: editPermission, allowed: canEditReceipt } = usePermission(() =>
  allOf(
    check(isMyReceipt.value, receipts.value?.length ? message.receiptOwner : message.stopOwner),
    check(!unref(isDeletingActualStop), message.deleting),
  ),
)

const store = useStore()
// Clearing receiver receipts is treated as an edit, so it stays allowed on completed jobs
const isReceiverReceipt = computed(
  () =>
    actualStop.value?.stopType === JobStopType.DropOff &&
    receiptType.value === InventoryReceiptType.Receiver,
)

// Clearing receiver receipts is allowed on completed jobs; deleting the stop is not.
const { permission: deletePermission, allowed: canDeleteReceipt } = usePermission(() =>
  allOf(
    editPermission.value,
    check(isReceiverReceipt.value || !isJobCompleted.value, message.completed),
  ),
)
const getUserBySid = computed<(sid: string | null | undefined) => User | null | undefined>(
  () => store.getters['users/getBySid'],
)

const locationItem = (locationName: string | null | undefined) => ({
  key: 'location',
  icon: 'mdi-map-marker-outline',
  title: 'Location',
  subtitles: [locationName],
})

const { allInventoryTypes } = useInventoryTypes()

const inventoryItem = (inventory: InventoryItem[]) => {
  const displayInventory = buildDisplayInventory(inventory, allInventoryTypes.value ?? []).map(
    buildInventoryDisplayString,
  )

  return {
    key: 'inventoryItem',
    icon: 'mdi-package-variant-closed',
    title: 'Item',
    subtitles: displayInventory,
  }
}

const actualStopListItems = computed(() =>
  [
    locationItem(actualStop.value?.locationName),
    receipts.value && receipts.value.length > 0 ? inventoryItem(receipts.value ?? []) : null,
    {
      key: 'date',
      icon: 'mdi-calendar-outline',
      title: 'Date',
      subtitles: [
        receipts.value && receipts.value.length > 0
          ? getReceiptTime(receipts.value[0])
          : actualStop.value?.arrivedAt.format('LLLL'),
      ],
    },
    {
      key: 'certification',
      icon: 'mdi-account-circle-outline',
      title: 'Certified by',
      subtitles: [
        receipts.value && receipts.value.length > 0
          ? getUserBySid.value(receipts.value[0]?.createdBy)?.name
          : getUserBySid.value(actualStop.value?.createdBy)?.name,
      ],
    },
  ].filter(Boolean),
)

const requestedStopListItems = computed(() => {
  return [
    locationItem(requestedStop.value?.locationName),
    inventoryItem(requestedStopInventory.value),
  ]
})

const dismiss = () => {
  jobBottomSheetStatesStore.clearJobBottomSheetStates()
}

const editClick = () => {
  if (!canEditReceipt.value) return
  // Clearing the state affects the actual/requested computed so we capture them first
  const actual = actualStop.value
  const requested = requestedStop.value ?? null
  const stateObj = {
    isVisible: true,
    requestedStop: requested,
    actualStop: actual,
    receiptType: receiptType.value,
  }
  jobBottomSheetStatesStore.clearJobBottomSheetStates()
  jobBottomSheetStatesStore.setJobBottomSheetStates({
    stopConfirmationBottomSheetStates: stateObj,
  })
}

const deleteClick = async () => {
  if (!canDeleteReceipt.value || !actualStop.value?.id) {
    return
  }

  try {
    const stopId = actualStop.value.id
    if (
      actualStop.value.stopType === JobStopType.DropOff &&
      receiptType.value === InventoryReceiptType.Receiver
    ) {
      if (!window.confirm('Are you sure you want to delete your confirmation from this stop?')) {
        return
      }

      await updateStopReceipts(
        props.jobId,
        stopId,
        [],
        moment().utc(),
        InventoryReceiptType.Receiver,
      )
    } else {
      if (!window.confirm('Are you sure you want to delete all confirmations from this stop?')) {
        return
      }

      await deleteActualStop({
        jobId: props.jobId,
        stopId,
      })
    }

    await Promise.all([
      queryClient.invalidateQueries(['stops', 'byJobId', props.jobId]),
      ...(stopId ? [queryClient.invalidateQueries(['stops', 'byId', stopId])] : []),
    ])

    queryClient.invalidateQueries(['history', 'byJobNumber', props.jobNumber])

    dismiss()
  } catch {
    // Keep the bottom sheet open if the delete fails, but let the user know.
    window.alert('Unable to delete this stop. Please try again.')
  }
}
</script>

<style scoped>
.v-list-item__subtitle {
  white-space: normal;
  overflow-wrap: anywhere;
}
</style>
